// §7.6.21 FaxConfigPage — admin page for managing eFax accounts and inbound inbox configs.
//
// Route: /admin/fax-config
// Roles: medflow_admin
//
// Layout:
//   PageHeader "Fax Configuration"
//   TabBar — "eFax Accounts" | "Inbound Inboxes"
//   DataTable for selected tab
//   Create/edit modals for each entity type

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../services/api'
import {
  PageHeader,
  DataTable,
  Alert,
  Modal,
  TabBar,
  FormField,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  colors,
  input,
  label as labelStyle,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EfaxConfigRow {
  readonly id: string
  readonly organizationId: string
  readonly efaxAppId: string
  readonly efaxUserId: string
  readonly enabled: boolean
  readonly createdAt: string
}

interface InboxConfigRow {
  readonly id: string
  readonly name: string
  readonly providerFaxNumber: string
  readonly enabled: boolean
  readonly lastReceivedAt: string | null
  readonly createdAt: string
}

interface EfaxConfigForm {
  efaxAppId: string
  efaxApiKey: string
  efaxUserId: string
  enabled: boolean
}

interface InboxConfigForm {
  name: string
  providerFaxNumber: string
  webhookSecret: string
  parserConfig: string
  enabled: boolean
  defaultTestTypeId: string
  defaultCreatedById: string
  defaultSiteId: string
}

interface TestParserResult {
  readonly [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTs(ts: string | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleString()
}

const EMPTY_EFAX_FORM: EfaxConfigForm = {
  efaxAppId: '',
  efaxApiKey: '',
  efaxUserId: '',
  enabled: true,
}

const EMPTY_INBOX_FORM: InboxConfigForm = {
  name: '',
  providerFaxNumber: '',
  webhookSecret: '',
  parserConfig: '',
  enabled: true,
  defaultTestTypeId: '',
  defaultCreatedById: '',
  defaultSiteId: '',
}

// ---------------------------------------------------------------------------
// eFax Accounts Tab
// ---------------------------------------------------------------------------

interface EfaxTabProps {
  readonly refreshTick: number
}

function EfaxTab({ refreshTick }: EfaxTabProps) {
  const [rows, setRows] = useState<readonly EfaxConfigRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<EfaxConfigForm>(EMPTY_EFAX_FORM)
  const [saving, setSaving] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('fax-efax-configs')

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<{ rows: EfaxConfigRow[] }>('/admin/fax/efax-configs')
      setRows(res.data?.rows ?? [])
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load eFax configs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh, refreshTick])

  function openNew() {
    setEditingId(null)
    setForm(EMPTY_EFAX_FORM)
    setModalOpen(true)
  }

  function openEdit(row: EfaxConfigRow) {
    setEditingId(row.id)
    setForm({
      efaxAppId: row.efaxAppId,
      efaxApiKey: '',
      efaxUserId: row.efaxUserId,
      enabled: row.enabled,
    })
    setModalOpen(true)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        efaxAppId: form.efaxAppId,
        efaxUserId: form.efaxUserId,
        enabled: form.enabled,
      }
      if (form.efaxApiKey) body.efaxApiKey = form.efaxApiKey

      if (editingId) {
        await api.patch(`/admin/fax/efax-configs/${editingId}`, body)
      } else {
        body.efaxApiKey = form.efaxApiKey
        await api.post('/admin/fax/efax-configs', body)
      }
      setModalOpen(false)
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const columns: readonly Column<EfaxConfigRow>[] = [
    { key: 'efaxAppId', header: 'App ID', sortable: true, render: (r) => <code>{r.efaxAppId}</code> },
    { key: 'efaxUserId', header: 'User ID', sortable: true, render: (r) => r.efaxUserId },
    {
      key: 'enabled',
      header: 'Enabled',
      render: (r) => <StatusBadge status={r.enabled ? 'enabled' : 'disabled'} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <button
          type="button"
          style={{ ...btnSecondary, fontSize: '0.75rem', padding: '2px 8px' }}
          onClick={(e) => {
            e.stopPropagation()
            openEdit(r)
          }}
        >
          Edit
        </button>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '1rem 0 0.5rem' }}>
        <ColumnSettingsButton columns={columns} {...tableColumnProps} />
        <button type="button" onClick={openNew} style={{ ...btnPrimary, marginLeft: '0.5rem' }}>
          Add eFax Config
        </button>
      </div>

      {error && <Alert type="error" message={error} />}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows as EfaxConfigRow[]}
          keyField="id"
          onRowClick={openEdit}
          emptyMessage="No eFax accounts configured."
          {...tableColumnProps}
        />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit eFax Account' : 'Add eFax Account'}
        width="480px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <FormField
            name="efaxAppId"
            label="App ID"
            required
            value={form.efaxAppId}
            onChange={(v) => setForm((f) => ({ ...f, efaxAppId: v }))}
            placeholder="com.example.app"
          />
          <FormField
            name="efaxApiKey"
            label={editingId ? 'API Key (leave blank to keep current)' : 'API Key'}
            type="password"
            required={!editingId}
            value={form.efaxApiKey}
            onChange={(v) => setForm((f) => ({ ...f, efaxApiKey: v }))}
          />
          <FormField
            name="efaxUserId"
            label="User ID"
            required
            value={form.efaxUserId}
            onChange={(v) => setForm((f) => ({ ...f, efaxUserId: v }))}
            placeholder="user@example.com"
          />
          <div>
            <label style={labelStyle}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                style={{ marginRight: '0.5rem' }}
              />
              Enabled
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" style={btnSecondary} onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="button" style={btnPrimary} onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inbound Inboxes Tab
// ---------------------------------------------------------------------------

interface InboxTabProps {
  readonly refreshTick: number
}

function InboxTab({ refreshTick }: InboxTabProps) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<readonly InboxConfigRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<InboxConfigForm>(EMPTY_INBOX_FORM)
  const [saving, setSaving] = useState(false)

  // Test parser state
  const [testLines, setTestLines] = useState('')
  const [testResult, setTestResult] = useState<TestParserResult | null>(null)
  const [testError, setTestError] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)

  const { tableId: _, ...tableColumnProps } = useTableColumns('fax-inbox-configs')

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<{ rows: InboxConfigRow[] }>('/admin/fax/inbox-configs')
      setRows(res.data?.rows ?? [])
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load inbox configs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh, refreshTick])

  function openNew() {
    setEditingId(null)
    setForm(EMPTY_INBOX_FORM)
    setTestLines('')
    setTestResult(null)
    setTestError(null)
    setModalOpen(true)
  }

  function openEdit(row: InboxConfigRow) {
    setEditingId(row.id)
    setForm({
      name: row.name,
      providerFaxNumber: row.providerFaxNumber,
      webhookSecret: '',
      parserConfig: '',
      enabled: row.enabled,
      defaultTestTypeId: '',
      defaultCreatedById: '',
      defaultSiteId: '',
    })
    setTestLines('')
    setTestResult(null)
    setTestError(null)
    setModalOpen(true)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      let parsedConfig: unknown = undefined
      if (form.parserConfig.trim()) {
        try {
          parsedConfig = JSON.parse(form.parserConfig)
        } catch {
          setError('Parser config is not valid JSON')
          setSaving(false)
          return
        }
      }

      const body: Record<string, unknown> = {
        name: form.name,
        providerFaxNumber: form.providerFaxNumber,
        enabled: form.enabled,
      }
      if (form.webhookSecret) body.webhookSecret = form.webhookSecret
      if (parsedConfig !== undefined) body.parserConfig = parsedConfig
      if (form.defaultTestTypeId) body.defaultTestTypeId = form.defaultTestTypeId
      if (form.defaultCreatedById) body.defaultCreatedById = form.defaultCreatedById
      if (form.defaultSiteId) body.defaultSiteId = form.defaultSiteId

      if (editingId) {
        await api.patch(`/admin/fax/inbox-configs/${editingId}`, body)
      } else {
        await api.post('/admin/fax/inbox-configs', body)
      }
      setModalOpen(false)
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleTestParser() {
    setTesting(true)
    setTestError(null)
    setTestResult(null)
    try {
      let parsedConfig: unknown = undefined
      if (form.parserConfig.trim()) {
        try {
          parsedConfig = JSON.parse(form.parserConfig)
        } catch {
          setTestError('Parser config is not valid JSON')
          setTesting(false)
          return
        }
      }
      const res = await api.post<TestParserResult>('/admin/fax/test-parser', {
        ocrLines: testLines.split('\n').filter((l) => l.trim()),
        parserConfig: parsedConfig,
      })
      setTestResult(res.data ?? null)
    } catch (err) {
      setTestError(err instanceof ApiError ? err.message : 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  const columns: readonly Column<InboxConfigRow>[] = [
    { key: 'name', header: 'Name', sortable: true, render: (r) => <strong>{r.name}</strong> },
    { key: 'providerFaxNumber', header: 'Fax Number', render: (r) => r.providerFaxNumber },
    {
      key: 'enabled',
      header: 'Enabled',
      render: (r) => <StatusBadge status={r.enabled ? 'enabled' : 'disabled'} />,
    },
    {
      key: 'lastReceivedAt',
      header: 'Last Received',
      render: (r) => formatTs(r.lastReceivedAt),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            type="button"
            style={{ ...btnSecondary, fontSize: '0.75rem', padding: '2px 8px' }}
            onClick={(e) => {
              e.stopPropagation()
              openEdit(r)
            }}
          >
            Edit
          </button>
          <button
            type="button"
            style={{ ...btnSecondary, fontSize: '0.75rem', padding: '2px 8px' }}
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/admin/fax-import-logs/${r.id}`)
            }}
          >
            View Logs
          </button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '1rem 0 0.5rem' }}>
        <ColumnSettingsButton columns={columns} {...tableColumnProps} />
        <button type="button" onClick={openNew} style={{ ...btnPrimary, marginLeft: '0.5rem' }}>
          Add Inbox
        </button>
      </div>

      {error && <Alert type="error" message={error} />}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows as InboxConfigRow[]}
          keyField="id"
          onRowClick={openEdit}
          emptyMessage="No inbound inboxes configured."
          {...tableColumnProps}
        />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit Inbound Inbox' : 'Add Inbound Inbox'}
        width="600px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <FormField
            name="name"
            label="Name"
            required
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: v }))}
            placeholder="Main Lab Inbox"
          />
          <FormField
            name="providerFaxNumber"
            label="Fax Number"
            required
            value={form.providerFaxNumber}
            onChange={(v) => setForm((f) => ({ ...f, providerFaxNumber: v }))}
            placeholder="+15555551234"
          />
          <FormField
            name="webhookSecret"
            label={editingId ? 'Webhook Secret (leave blank to keep current)' : 'Webhook Secret'}
            type="password"
            value={form.webhookSecret}
            onChange={(v) => setForm((f) => ({ ...f, webhookSecret: v }))}
          />
          <div>
            <label htmlFor="parserConfig" style={labelStyle}>
              Parser Config (JSON)
            </label>
            <textarea
              id="parserConfig"
              style={{
                ...input,
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                resize: 'vertical',
              }}
              value={form.parserConfig}
              onChange={(e) => setForm((f) => ({ ...f, parserConfig: e.target.value }))}
              rows={6}
              placeholder='{"fields": [{"name": "patientName", "pattern": "Patient:\\s*(.+)"}]}'
            />
          </div>
          <div>
            <label style={labelStyle}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                style={{ marginRight: '0.5rem' }}
              />
              Enabled
            </label>
          </div>

          <div
            style={{
              borderTop: `1px solid ${colors.border}`,
              paddingTop: '1rem',
              marginTop: '0.5rem',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: colors.text,
                marginBottom: '0.75rem',
              }}
            >
              Default Assignments (optional UUIDs)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <FormField
                name="defaultTestTypeId"
                label="Default Test Type ID"
                value={form.defaultTestTypeId}
                onChange={(v) => setForm((f) => ({ ...f, defaultTestTypeId: v }))}
                placeholder="UUID"
              />
              <FormField
                name="defaultCreatedById"
                label="Default Created By (User ID)"
                value={form.defaultCreatedById}
                onChange={(v) => setForm((f) => ({ ...f, defaultCreatedById: v }))}
                placeholder="UUID"
              />
              <FormField
                name="defaultSiteId"
                label="Default Site ID"
                value={form.defaultSiteId}
                onChange={(v) => setForm((f) => ({ ...f, defaultSiteId: v }))}
                placeholder="UUID"
              />
            </div>
          </div>

          <div
            style={{
              borderTop: `1px solid ${colors.border}`,
              paddingTop: '1rem',
              marginTop: '0.5rem',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: colors.text,
                marginBottom: '0.75rem',
              }}
            >
              Test Parser Config
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="testLines" style={labelStyle}>
                OCR Lines (one per line)
              </label>
              <textarea
                id="testLines"
                style={{
                  ...input,
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                  resize: 'vertical',
                }}
                value={testLines}
                onChange={(e) => setTestLines(e.target.value)}
                rows={5}
                placeholder={'Patient: John Doe\nDOB: 01/01/1980\nTest: CBC'}
              />
            </div>
            <button
              type="button"
              style={btnSecondary}
              onClick={() => void handleTestParser()}
              disabled={testing || !testLines.trim()}
            >
              {testing ? 'Testing...' : 'Test'}
            </button>
            {testError && (
              <div
                style={{
                  marginTop: '0.5rem',
                  color: colors.danger,
                  fontSize: '0.875rem',
                }}
              >
                {testError}
              </div>
            )}
            {testResult && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem', color: colors.text }}>
                  Parsed Result:
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <tbody>
                    {Object.entries(testResult).map(([k, v]) => (
                      <tr key={k}>
                        <td
                          style={{
                            padding: '3px 8px',
                            fontWeight: 500,
                            color: colors.textMuted,
                            borderBottom: `1px solid ${colors.border}`,
                            width: '35%',
                          }}
                        >
                          {k}
                        </td>
                        <td
                          style={{
                            padding: '3px 8px',
                            fontFamily: 'monospace',
                            borderBottom: `1px solid ${colors.border}`,
                          }}
                        >
                          {JSON.stringify(v)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" style={btnSecondary} onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="button" style={btnPrimary} onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FaxConfigPage (top-level)
// ---------------------------------------------------------------------------

type ActiveTab = 'efax' | 'inboxes'

export function FaxConfigPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('efax')
  const [refreshTick, setRefreshTick] = useState(0)

  const tabs = [
    { key: 'efax', label: 'eFax Accounts' },
    { key: 'inboxes', label: 'Inbound Inboxes' },
  ]

  return (
    <div>
      <PageHeader
        title="Fax Configuration"
        subtitle="Manage eFax accounts and inbound inbox configurations"
        actions={
          <button type="button" style={btnSecondary} onClick={() => setRefreshTick((t) => t + 1)}>
            Refresh
          </button>
        }
      />

      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={(k) => setActiveTab(k as ActiveTab)} />

      {activeTab === 'efax' && <EfaxTab refreshTick={refreshTick} />}
      {activeTab === 'inboxes' && <InboxTab refreshTick={refreshTick} />}
    </div>
  )
}
